import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Inbox, Loader2, RefreshCw, Check, X, AlertTriangle, FileSpreadsheet,
  SunMedium, Package, Megaphone, ChevronDown, ChevronUp, ShieldAlert,
} from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';

const API = '/api';

const ITEM_TYPE_META = {
  quote_request: { icon: FileSpreadsheet, label: 'Offerteaanvraag', cls: 'bg-amber-50 text-amber-800 border-amber-200' },
  ec_lead: { icon: SunMedium, label: 'Energy Control lead', cls: 'bg-orange-50 text-orange-800 border-orange-200' },
  affiliate_product: { icon: Package, label: 'Affiliate product', cls: 'bg-blue-50 text-blue-800 border-blue-200' },
  ads_campaign: { icon: Megaphone, label: 'Ads campagne', cls: 'bg-purple-50 text-purple-800 border-purple-200' },
};

const FILTERS = [
  { value: 'all', label: 'Alle' },
  { value: 'quote_request', label: 'Quotes' },
  { value: 'ec_lead', label: 'EC leads' },
  { value: 'affiliate_product', label: 'Producten' },
  { value: 'ads_campaign', label: 'Ads' },
];

function ItemRow({ item, onAction, expanded, onToggleExpand }) {
  const meta = ITEM_TYPE_META[item.item_type] || ITEM_TYPE_META.quote_request;
  const Icon = meta.icon;
  const detailKeys = Object.keys(item.details || {}).filter((k) => item.details[k] !== null && item.details[k] !== '' && item.details[k] !== undefined);

  return (
    <div className="bg-white border border-border rounded-lg overflow-hidden" data-testid={`inbox-item-${item.item_id}`}>
      <button
        onClick={onToggleExpand}
        className="w-full text-left p-3 sm:p-4 hover:bg-muted/30 transition-colors"
        data-testid={`inbox-item-toggle-${item.item_id}`}
      >
        <div className="flex items-start gap-3">
          <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-lg border flex items-center justify-center flex-shrink-0 ${meta.cls}`}>
            <Icon className="w-4 h-4 sm:w-5 sm:h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className={`text-[10px] px-1.5 py-0.5 rounded border ${meta.cls}`}>{meta.label}</span>
              {item.external_action_required && (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-rose-50 text-rose-700 border border-rose-200 inline-flex items-center gap-0.5" data-testid={`inbox-item-external-${item.item_id}`}>
                  <ShieldAlert className="w-2.5 h-2.5" /> Aparte GO
                </span>
              )}
              <span className="text-[10px] text-muted-foreground">{item.current_status || '—'}</span>
            </div>
            <div className="text-sm font-medium mt-1 truncate" data-testid={`inbox-item-label-${item.item_id}`}>
              {item.label || '—'}
            </div>
            {item.created_at && (
              <div className="text-[10px] sm:text-[11px] text-muted-foreground mt-0.5">
                {new Date(item.created_at).toLocaleString('nl-NL', { dateStyle: 'short', timeStyle: 'short' })}
              </div>
            )}
          </div>
          <div className="flex-shrink-0 text-muted-foreground">
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </div>
        </div>
      </button>

      {expanded && (
        <div className="border-t border-border bg-muted/20 px-3 sm:px-4 py-3" data-testid={`inbox-item-details-${item.item_id}`}>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-[11px] sm:text-xs mb-3">
            {detailKeys.map((k) => (
              <div key={k} className="flex gap-1.5">
                <dt className="text-muted-foreground capitalize">{k.replace(/_/g, ' ')}:</dt>
                <dd className="font-medium truncate flex-1 min-w-0">{String(item.details[k]).slice(0, 200)}</dd>
              </div>
            ))}
          </dl>
          {item.external_action_required && (
            <div className="text-[11px] bg-rose-50 border border-rose-200 rounded-md px-2.5 py-1.5 text-rose-900 mb-3 leading-relaxed" data-testid={`inbox-item-warning-${item.item_id}`}>
              <strong>Let op:</strong> {item.external_action_label}. Approve update alléén interne status + maakt een to-do voor je aan. Externe actie blijft handmatig.
            </div>
          )}
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => onAction(item, 'approve')}
              className="px-3 py-1.5 text-xs bg-emerald-600 text-white rounded-md hover:bg-emerald-700 flex items-center gap-1.5"
              data-testid={`inbox-approve-${item.item_id}`}
            >
              <Check className="w-3 h-3" /> Approve
            </button>
            <button
              onClick={() => onAction(item, 'reject')}
              className="px-3 py-1.5 text-xs bg-rose-600 text-white rounded-md hover:bg-rose-700 flex items-center gap-1.5"
              data-testid={`inbox-reject-${item.item_id}`}
            >
              <X className="w-3 h-3" /> Reject
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function ConfirmModal({ item, action, onClose, onConfirm }) {
  const [note, setNote] = useState('');
  const [generateTodo, setGenerateTodo] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  if (!item) return null;

  const meta = ITEM_TYPE_META[item.item_type] || ITEM_TYPE_META.quote_request;
  const isApprove = action === 'approve';

  const submit = async () => {
    setSubmitting(true);
    try {
      await onConfirm(note, generateTodo);
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="inbox-confirm-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isApprove ? <Check className="w-4 h-4 text-emerald-600" /> : <X className="w-4 h-4 text-rose-600" />}
            {isApprove ? 'Approve' : 'Reject'} — {meta.label}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="bg-muted/40 rounded-md p-2.5">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Item</div>
            <div className="font-medium mt-0.5 break-words">{item.label}</div>
          </div>

          {item.external_action_required && (
            <div className="bg-rose-50 border border-rose-200 rounded-md px-2.5 py-2 text-[11px] text-rose-900 flex items-start gap-2" data-testid="inbox-confirm-external-warning">
              <ShieldAlert className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
              <div>
                <strong>Aparte GO vereist voor:</strong> {item.external_action_label}.<br />
                Deze knop updatet alléén interne status en maakt een to-do aan. Geen externe call.
              </div>
            </div>
          )}

          <div>
            <label className="text-[11px] text-muted-foreground uppercase tracking-wider">Notitie (optioneel)</label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              placeholder={isApprove ? 'Bv. reden voor approve' : 'Bv. reden voor reject'}
              className="w-full mt-1 border border-border rounded-md px-2.5 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary"
              data-testid="inbox-confirm-note"
            />
          </div>
          <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
            <input
              type="checkbox"
              checked={generateTodo}
              onChange={(e) => setGenerateTodo(e.target.checked)}
              data-testid="inbox-confirm-todo-checkbox"
            />
            <span>Voeg automatisch een to-do toe (volgende stap voor mij)</span>
          </label>
        </div>
        <DialogFooter className="gap-2">
          <button onClick={onClose} className="px-3 py-1.5 text-sm border border-border rounded-md hover:bg-muted" data-testid="inbox-confirm-cancel">
            Annuleren
          </button>
          <button
            onClick={submit}
            disabled={submitting}
            className={`px-3 py-1.5 text-sm text-white rounded-md disabled:opacity-50 flex items-center gap-1.5 ${isApprove ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-rose-600 hover:bg-rose-700'}`}
            data-testid="inbox-confirm-submit"
          >
            {submitting ? <Loader2 className="w-3 h-3 animate-spin" /> : (isApprove ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />)}
            Bevestigen
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function ApprovalInboxTab({ token }) {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [labels, setLabels] = useState({});
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all');
  const [expandedId, setExpandedId] = useState(null);
  const [confirmTarget, setConfirmTarget] = useState(null); // { item, action }

  const authH = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = filter === 'all'
        ? `${API}/admin/control-center/approval-inbox?limit=60`
        : `${API}/admin/control-center/approval-inbox?item_type=${filter}&limit=60`;
      const { data } = await axios.get(url, { headers: authH });
      setItems(data.items || []);
      setCounts(data.counts || {});
      setLabels(data.labels || {});
    } catch (e) {
      toast.error('Kon inbox niet laden');
    }
    setLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, filter]);

  useEffect(() => { load(); }, [load]);

  const doAction = async (note, generateTodo) => {
    const { item, action } = confirmTarget;
    try {
      const { data } = await axios.post(
        `${API}/admin/control-center/approval-inbox/${item.item_type}/${item.item_id}/${action}`,
        { note, generate_todo: generateTodo },
        { headers: authH },
      );
      const verb = action === 'approve' ? 'Approved' : 'Rejected';
      const todoMsg = data.todo ? ' · to-do toegevoegd' : '';
      toast.success(`${verb} → ${data.new_status}${todoMsg}`);
      setExpandedId(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `${action} mislukt`);
    }
  };

  const totalCount = Object.values(counts).reduce((a, b) => a + (b || 0), 0);

  return (
    <div className="space-y-4" data-testid="approval-inbox-tab">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h2 className="text-base sm:text-lg font-medium flex items-center gap-2">
          <Inbox className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
          Approval Inbox
          <span className="text-xs text-muted-foreground" data-testid="inbox-total-count">({totalCount} open)</span>
        </h2>
        <button
          onClick={load}
          disabled={loading}
          className="px-2.5 py-1.5 text-xs border border-border rounded-md hover:bg-muted flex items-center gap-1.5"
          data-testid="inbox-refresh-btn"
        >
          {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
          Refresh
        </button>
      </div>

      {/* Safety banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 text-[11px] sm:text-xs text-blue-900" data-testid="inbox-safety-banner">
        <strong>Veiligheid:</strong> Approve hier updatet alleen interne status + maakt een to-do voor jou. Geen externe calls (geen klant-mail, geen provider-mail, geen Ads launch, geen Daisycon publish, geen Stripe charge).
      </div>

      {/* Filter chips */}
      <div className="flex gap-1.5 sm:gap-2 flex-wrap" data-testid="inbox-filters">
        {FILTERS.map((f) => (
          <button
            key={f.value}
            onClick={() => setFilter(f.value)}
            className={`px-2.5 py-1 rounded-full text-[11px] sm:text-xs font-medium border transition-colors ${
              filter === f.value
                ? 'bg-primary text-white border-primary'
                : 'bg-white border-border text-foreground hover:bg-muted'
            }`}
            data-testid={`inbox-filter-${f.value}`}
          >
            {f.label}
            {f.value !== 'all' && counts[f.value] != null && (
              <span className="ml-1 opacity-70">({counts[f.value]})</span>
            )}
            {f.value === 'all' && totalCount > 0 && (
              <span className="ml-1 opacity-70">({totalCount})</span>
            )}
          </button>
        ))}
      </div>

      {/* Items list */}
      {loading && items.length === 0 ? (
        <div className="flex items-center justify-center py-12" data-testid="inbox-loading">
          <Loader2 className="w-5 h-5 animate-spin text-primary" />
        </div>
      ) : items.length === 0 ? (
        <div className="bg-white border border-dashed border-border rounded-lg p-8 text-center" data-testid="inbox-empty">
          <Inbox className="w-8 h-8 text-muted-foreground/40 mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">Geen openstaande items voor dit filter.</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="inbox-items-list">
          {items.map((item) => (
            <ItemRow
              key={`${item.item_type}-${item.item_id}`}
              item={item}
              expanded={expandedId === `${item.item_type}-${item.item_id}`}
              onToggleExpand={() => setExpandedId(
                expandedId === `${item.item_type}-${item.item_id}`
                  ? null
                  : `${item.item_type}-${item.item_id}`,
              )}
              onAction={(it, action) => setConfirmTarget({ item: it, action })}
            />
          ))}
        </div>
      )}

      {confirmTarget && (
        <ConfirmModal
          item={confirmTarget.item}
          action={confirmTarget.action}
          onClose={() => setConfirmTarget(null)}
          onConfirm={doAction}
        />
      )}
    </div>
  );
}
